import React, { useMemo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTable, useSortBy, useFilters } from 'react-table';
import { ProfessionalName } from '@/components/common/ProfessionalPhoto';
import HoursDisplay from '@/components/data/hours/HoursDisplay';
import StatusIndicator from '@/components/data/hours/StatusIndicator';
import ProfessionalNotesHomepage from '@/components/data/professionals/ProfessionalNotesHomepage';
import ProfessionalSkills from '@/components/data/professionals/ProfessionalSkills';
import { professionalengagementsCss } from '@digital_monorepo/styling';

const IconDate = () => <span className='week-indicator' aria-hidden='true' />;

const NO_EXTRA_COLUMNS = [];

const STATUS_FILTER_OPTIONS = ['', 'green', 'yellow', 'red', 'blank'];

// Groups the already filtered/sorted table rows by a field. Groups are ordered
// by key ascending, so sortable keys (dates, "YYYY-MM") always run oldest
// first; rows inside each group keep the table's own sort.
const groupRows = (rows, field) => {
  const groups = new Map();
  rows.forEach(row => {
    const key = row.original[field] ?? '';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  });
  return [...groups.entries()].sort(([a], [b]) => String(a).localeCompare(String(b)));
};

const FreeHoursReport = ({
  date,
  title,
  weekId,
  report,
  leadingColumns = NO_EXTRA_COLUMNS,
  sharedFilters = {},
  onFilterChange = () => {},
  onStatusChange,
  onNotesChange,
  groupBy,
  formatGroup = value => value,
  titleSize
}) => {
  const { t } = useTranslation();
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const toggleGroup = key =>
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const columns = useMemo(
    () => [
      ...leadingColumns,
      {
        Header: t('PROFESSIONALS_TABLE.HEADERS.AREA'),
        accessor: 'area',
        filter: 'text'
      },
      {
        Header: t('HOURS_REPORT_TABLE.HEADERS.PROFESSIONAL_ID'),
        accessor: 'ProfessionalID',
        Cell: ({ value }) => (
          <Link to={`/professionals/${value}`} target='_blank' rel='noopener noreferrer'>
            {value}
          </Link>
        ),
        filter: 'text'
      },
      {
        Header: t('HOURS_REPORT_TABLE.HEADERS.PROFESSIONAL_NAME'),
        accessor: 'ProfessionalName',
        filter: 'text',
        Cell: ({ row }) => (
          <Link to={`/professionals/${row.original.ProfessionalID}`}>
            <ProfessionalName professional={row.original} />
          </Link>
        )
      },
      {
        Header: t('HOURS_REPORT_TABLE.HEADERS.FUNCTION_NAME'),
        accessor: 'FunctionName',
        filter: 'text'
      },
      {
        Header: t('HOURS_REPORT_TABLE.HEADERS.OFFICE'),
        accessor: 'Office',
        filter: 'text'
      },
      {
        Header: t('HOURS_REPORT_TABLE.HEADERS.HOURS'),
        accessor: 'Hours',
        Cell: ({ value }) => <HoursDisplay hours={value} freetime />
      },
      {
        Header: t('HOURS_REPORT_TABLE.HEADERS.STATUS'),
        accessor: 'Status',
        filter: 'text',
        Cell: ({ row }) => (
          <StatusIndicator professionalID={row.original.ProfessionalID} status={row.original.Status} onChange={newStatus => onStatusChange?.(row.original.ProfessionalID, newStatus)} />
        )
      },
      {
        Header: t('HOURS_REPORT_TABLE.HEADERS.PROFESSIONAL_NOTES'),
        accessor: 'ProfessionalNotes',
        Cell: ({ row }) => (
          <ProfessionalNotesHomepage
            professionalID={row.original.ProfessionalID}
            professionalNotes={row.original.ProfessionalNotes}
            onChange={newNotes => onNotesChange?.(row.original.ProfessionalID, newNotes)}
          />
        )
      },
      {
        Header: t('HOURS_REPORT_TABLE.HEADERS.SKILLS'),
        accessor: 'Skills',
        Cell: ({ row }) => <ProfessionalSkills professionalID={row.original.ProfessionalID} skills={row.original.Skills} />
      },
      {
        Header: t('HOURS_REPORT_TABLE.HEADERS.PROJECT'),
        accessor: 'EngagementName',
        filter: 'text'
      }
    ],
    [t, onStatusChange, onNotesChange, leadingColumns]
  );

  const data = useMemo(() => report, [report]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setFilter, setAllFilters } = useTable(
    {
      columns,
      data,
      autoResetFilters: false,
      autoResetSortBy: false,
      initialState: { filters: Object.entries(sharedFilters).map(([id, value]) => ({ id, value })) }
    },
    useFilters,
    useSortBy
  );

  // Handle filter changes by propagating to shared state
  const handleFilterChange = (columnId, value) => {
    // Update local table filter
    setFilter(columnId, value);
    // Propagate to parent for other tables
    onFilterChange(columnId, value);
  };

  // Update local filters when shared filters change
  useEffect(() => {
    setAllFilters(
      Object.entries(sharedFilters)
        .filter(([, value]) => value !== undefined && value !== '')
        .map(([id, value]) => ({ id, value }))
    );
  }, [sharedFilters, setAllFilters]);

  const renderRow = row => {
    prepareRow(row);
    const { key, ...restRowProps } = row.getRowProps();
    return (
      <tr key={key} {...restRowProps} className={professionalengagementsCss.bodyRow}>
        {row.cells.map(cell => {
          const { key: cellKey, ...restCellProps } = cell.getCellProps();
          return (
            <td key={cellKey} {...restCellProps} className={professionalengagementsCss.td} data-column={cell.column.id}>
              {cell.render('Cell')}
            </td>
          );
        })}
      </tr>
    );
  };

  return (
    <div id={weekId} className={`${professionalengagementsCss.sectionContainer} availability-week`}>
      <div className={professionalengagementsCss.sectionHeader}>
        <h3 className={professionalengagementsCss.sectionTitle} style={titleSize ? { fontSize: titleSize } : undefined}>
          <IconDate />
          {title || `Week of ${date}`}
        </h3>
        <span className='workspace-count'>{rows.length} professionals</span>
      </div>
      <div className={professionalengagementsCss.tableWrapper}>
        <div className={`${professionalengagementsCss.tableContainer} ${professionalengagementsCss.overviewTableContainer}`}>
          <table {...getTableProps()} className={professionalengagementsCss.table}>
            <thead>
              {headerGroups.map(headerGroup => {
                const { key, ...restHeaderGroupProps } = headerGroup.getHeaderGroupProps();
                return (
                  <tr key={key} {...restHeaderGroupProps} className={professionalengagementsCss.headerRow}>
                    {headerGroup.headers.map(column => {
                      const { key, ...restColumn } = column.getHeaderProps(column.getSortByToggleProps());
                      return (
                        <th key={key} {...restColumn} className={professionalengagementsCss.th} data-column={column.id}>
                          <div style={{ marginBottom: '0.5rem' }}>
                            {column.render('Header')}
                            <span>{column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}</span>
                          </div>
                          {column.canFilter && column.id === 'Status' && (
                            <select
                              aria-label={`Filter ${column.Header}`}
                              value={column.filterValue || ''}
                              onChange={e => handleFilterChange(column.id, e.target.value || undefined)}
                              style={{
                                width: '100%',
                                padding: '0.25rem',
                                fontSize: '0.75rem',
                                border: '1px solid #ddd',
                                borderRadius: '3px'
                              }}
                              onClick={e => e.stopPropagation()}>
                              {STATUS_FILTER_OPTIONS.map(option => (
                                <option key={option || 'all'} value={option}>
                                  {option ? t(`STATUS_INDICATOR.${option.toUpperCase()}`) : t('STATUS_INDICATOR.ALL')}
                                </option>
                              ))}
                            </select>
                          )}
                          {column.canFilter && column.id !== 'Status' && (
                            <input
                              type='text'
                              aria-label={`Filter ${column.Header}`}
                              value={column.filterValue || ''}
                              onChange={e => handleFilterChange(column.id, e.target.value || undefined)}
                              placeholder={`Filter...`}
                              style={{
                                width: '100%',
                                padding: '0.25rem',
                                fontSize: '0.75rem',
                                border: '1px solid #ddd',
                                borderRadius: '3px'
                              }}
                              onClick={e => e.stopPropagation()}
                            />
                          )}
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                    No free hours data matches the current filters
                  </td>
                </tr>
              ) : groupBy ? (
                groupRows(rows, groupBy).map(([groupKey, groupRowsList]) => {
                  const isExpanded = expandedGroups.has(groupKey);
                  return (
                    <React.Fragment key={`group-${groupKey}`}>
                      <tr className={`${professionalengagementsCss.bodyRow} free-from-group`} data-group={groupKey}>
                        <td colSpan={columns.length} className={professionalengagementsCss.td} style={{ background: '#f6f7f9', fontWeight: 600 }}>
                          {/* Button first and fixed-width, then fixed-width month, so
                              every group header lines up in the same columns. */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <button
                              type='button'
                              className='workspace-button'
                              aria-expanded={isExpanded}
                              onClick={() => toggleGroup(groupKey)}
                              style={{ width: '6rem', justifyContent: 'center', flexShrink: 0 }}>
                              {isExpanded ? '− Less' : '+ More'}
                            </button>
                            <span style={{ width: '10rem', flexShrink: 0 }}>{formatGroup(groupKey)}</span>
                            <span style={{ fontWeight: 400, color: '#666' }}>
                              {groupRowsList.length} {groupRowsList.length === 1 ? 'professional' : 'professionals'}
                            </span>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && groupRowsList.map(row => renderRow(row))}
                    </React.Fragment>
                  );
                })
              ) : (
                rows.map(row => renderRow(row))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

FreeHoursReport.propTypes = {
  date: PropTypes.string,
  title: PropTypes.string,
  leadingColumns: PropTypes.array,
  weekId: PropTypes.string,
  report: PropTypes.arrayOf(PropTypes.object).isRequired,
  sharedFilters: PropTypes.object,
  onFilterChange: PropTypes.func,
  onStatusChange: PropTypes.func,
  onNotesChange: PropTypes.func,
  groupBy: PropTypes.string,
  formatGroup: PropTypes.func,
  titleSize: PropTypes.string
};

export default FreeHoursReport;
